/**
 * Error Dashboard Component
 * Development/admin interface for viewing and managing errors
 */

'use client';

import React from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography'
import { Card, Table, Button, Tag, Space, Tooltip, Empty } from 'antd';
import { DeleteOutlined, ReloadOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useErrorContext } from '@/shared/components/error/ErrorContext';
import { formatTimeAgo } from '@/shared/utils/formatters';

const { Title, Text } = Typography;
const { Column } = Table;

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface ErrorDashboardProps {
  /** Whether to show in compact mode */
  compact?: boolean;
  /** Maximum number of errors to display */
  maxErrors?: number;
  /** Custom CSS classes */
  className?: string;
}

// =============================================================================
// ERROR DASHBOARD COMPONENT
// =============================================================================

export const ErrorDashboard: React.FC<ErrorDashboardProps> = ({
  compact = false,
  maxErrors = 50,
  className,
}) => {
  const { errors, clearErrors, retryErrorOperation } = useErrorContext();

  // Get recent errors (limited by maxErrors)
  const recentErrors = errors.slice(-maxErrors).reverse();

  // Handle retry operation
  const handleRetry = (errorId: string) => {
    retryErrorOperation(errorId);
  };

  // Get error type color
  const getErrorTypeColor = (errorName: string) => {
    switch (errorName) {
      case 'TradingError':
        return 'red';
      case 'NetworkError':
        return 'orange';
      case 'ValidationError':
        return 'yellow';
      case 'AuthenticationError':
        return 'purple';
      default:
        return 'default';
    }
  };

  // Render error details
  const renderErrorDetails = (record: any) => (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      <div>
        <Text strong>Message:</Text> {record.message}
      </div>
      {record.context && (
        <div>
          <Text strong>Context:</Text> {record.context}
        </div>
      )}
      {record.stack && (
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', color: '#1890ff' }}>
            View Stack Trace
          </summary>
          <pre style={{
            marginTop: '8px',
            fontSize: '11px',
            backgroundColor: '#f5f5f5',
            padding: '8px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '200px'
          }}>
            {record.stack}
          </pre>
        </details>
      )}
    </Space>
  );

  if (recentErrors.length === 0) {
    return (
      <Card className={className} title="Error Dashboard">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No errors recorded"
        />
      </Card>
    );
  }

  if (compact) {
    return (
      <Card
        className={className}
        title={
          <Space>
            <ExclamationCircleOutlined />
            <span>Recent Errors ({recentErrors.length})</span>
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<DeleteOutlined />}
            onClick={clearErrors}
            danger
          >
            Clear All
          </Button>
        }
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          {recentErrors.slice(0, 5).map((error) => (
            <div key={error.id} style={{
              padding: '8px',
              border: '1px solid #f0f0f0',
              borderRadius: '4px',
              backgroundColor: error.error.name === 'TradingError' ? '#fff2f0' : '#fafafa'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ color: error.error.name === 'TradingError' ? '#ff4d4f' : undefined }}>
                    {error.error.message}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatTimeAgo(error.timestamp)} • {error.context}
                  </Text>
                </div>
                <Space>
                  <Tag color={getErrorTypeColor(error.error.name)}>
                    {error.error.name}
                  </Tag>
                  {error.retryable && (
                    <Tooltip title="Retry Operation">
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => handleRetry(error.id)}
                        disabled={!error.retryFn}
                      />
                    </Tooltip>
                  )}
                </Space>
              </div>
            </div>
          ))}
        </Space>
      </Card>
    );
  }

  return (
    <Card
      className={className}
      title={
        <Space>
          <ExclamationCircleOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Error Dashboard
          </Title>
          <Tag color="red">{recentErrors.length} errors</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<DeleteOutlined />}
            onClick={clearErrors}
            danger
          >
            Clear All Errors
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={recentErrors}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} errors`,
        }}
        expandable={{
          expandedRowRender: renderErrorDetails,
          expandRowByClick: true,
        }}
      >
        <Column
          title="Severity"
          dataIndex="severity"
          key="severity"
          width={100}
          render={(severity: string) => (
            <Tag color={severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : severity === 'medium' ? 'yellow' : 'blue'}>
              {severity?.toUpperCase() || 'UNKNOWN'}
            </Tag>
          )}
          filters={[
            { text: 'Critical', value: 'critical' },
            { text: 'High', value: 'high' },
            { text: 'Medium', value: 'medium' },
            { text: 'Low', value: 'low' },
          ]}
          onFilter={(value, record) => record.severity === value}
        />

        <Column
          title="Message"
          dataIndex="message"
          key="message"
          render={(message: string) => (
            <Tooltip title={message}>
              <span style={{
                display: 'block',
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}>{message}</span>
            </Tooltip>
          )}
        />

        <Column
          title="Context"
          dataIndex="context"
          key="context"
          width={150}
          render={(context: string) => (
            <Tooltip title={context}>
              <span style={{
                color: 'rgba(0, 0, 0, 0.45)',
                display: 'block',
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}>{context}</span>
            </Tooltip>
          )}
        />

        <Column
          title="Time"
          dataIndex="timestamp"
          key="timestamp"
          width={120}
          render={(timestamp: Date) => (
            <Tooltip title={timestamp.toLocaleString()}>
              <Text type="secondary">{formatTimeAgo(timestamp)}</Text>
            </Tooltip>
          )}
          sorter={(a, b) => b.timestamp.getTime() - a.timestamp.getTime()}
          defaultSortOrder="descend"
        />

        <Column
          title="Actions"
          key="actions"
          width={120}
          render={(_, record: any) => (
            <Space>
              <Tooltip title="View Details">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => console.log('View error details:', record)}
                />
              </Tooltip>
              {record.retryable && (
                <Tooltip title="Retry Operation">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => handleRetry(record.id)}
                  />
                </Tooltip>
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};

export default ErrorDashboard;
